import React, { useState, useRef, SyntheticEvent } from 'react';
import { observer } from 'mobx-react';
import { useQuery } from '@apollo/client';

import { Button, IconSvg, useOutsideClick } from '@utex/ui-kit';

import { BalanceTooltip } from '../BalanceTooltip';
import { ViewType } from '../../../../types';
import query, { QueryInterface } from './query';

import { BalanceButton, getButtonColor, Container } from './styles';
import { getGrossSum, getInvestSum } from './helpers';
import { MoneyView } from '../../../MoneyView';

interface Props {
  view: ViewType;
  openDeposit: () => void;
  openWithdraw: () => void;
  onAccountsClick: () => void;
}

const useScreenStore = () => {
  const { loading, data } = useQuery<QueryInterface>(query, { errorPolicy: 'ignore' });

  const balances: {
    [key: string]: {
      value: number;
      currency: string;
    };
  } = {};

  if (!loading && data) {
    if (data.currentUser?.balances) {
      const userUsdBalance = data.currentUser.balances?.find(
        (balance) => balance.amount.currency.code === 'USD'
      );

      balances.freeUsdBalance = {
        value: Number(userUsdBalance?.amount?.value || 0),
        currency: 'USD',
      };
    }

    if (
      data.bnh?.positionsSummary &&
      data.limited?.positionsSummary &&
      data.unlimited?.positionsSummary &&
      data.stocks?.positionsSummary
    ) {
      const grossSum = getGrossSum(data);
      const investSum = getInvestSum(data);
      balances.grossSum = { value: grossSum, currency: 'USD' };
      balances.investSum = { value: investSum, currency: 'USD' };
    }

    if (balances.freeUsdBalance && balances.investSum && balances.grossSum) {
      const userBalanceSumm =
        balances.freeUsdBalance.value + balances.grossSum.value + balances.investSum.value;
      balances.userBalanceSumm = { value: userBalanceSumm, currency: 'USD' };
    }
  }

  return balances;
};

export const Balance: React.FC<Props> = observer(
  ({ view, openDeposit, openWithdraw, onAccountsClick }) => {
    const [isTooltipOpen, setTooltipOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    useOutsideClick(containerRef, isTooltipOpen, () => setTooltipOpen(false));
    const balances = useScreenStore();

    return (
      <Container ref={containerRef}>
        <BalanceButton
          data-testid="balance-btn"
          type="button"
          view={view}
          isTooltipOpen={isTooltipOpen}
          onClick={() => setTooltipOpen(true)}
        >
          <MoneyView rule="0-2">{balances.userBalanceSumm}</MoneyView>
        </BalanceButton>

        <Button
          shape="square"
          size="S"
          view={getButtonColor(view)}
          isBlured={view !== 'default'}
          onClick={(e: SyntheticEvent) => {
            e.preventDefault();
            openDeposit();
          }}
          data-testid="deposit-btn"
          data-gtm="deposit-plus-icon-btn"
        >
          <IconSvg src="plus" />
        </Button>

        <BalanceTooltip
          free={balances.freeUsdBalance}
          investedSum={balances.investSum}
          grossSum={balances.grossSum}
          isTooltipVisible={isTooltipOpen}
          openDeposit={openDeposit}
          openWithdraw={openWithdraw}
          onAccountsClick={onAccountsClick}
          view={view}
        />
      </Container>
    );
  }
);
